import axios from "axios";
import { authService } from "./auth.service";

const API_BASE_URL =
   "https://njengo-server-1.onrender.com/api";

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to add auth token
axiosInstance.interceptors.request.use(
  (config) => {
    const { token } = authService.getStoredAuth();
    console.log("API Request:", {
      url: config.url,
      hasToken: !!token,
      tokenLength: token?.length,
    });
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.error("API Error:", {
      status: error.response?.status,
      message: error.response?.data?.message,
      path: error.config?.url,
    });

    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh token yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh token
        await authService.refreshToken();
        const newToken = authService.getToken();

        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // If refresh fails, logout and redirect
        authService.logout();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

const apiService = {
  async get<T>(endpoint: string, params?: any): Promise<T> {
    const response = await axiosInstance.get(endpoint, { params });
    return response.data;
  },

  async post<T>(endpoint: string, data: any): Promise<T> {
    const response = await axiosInstance.post(endpoint, data);
    return response.data;
  },

  async put<T>(endpoint: string, data: any): Promise<T> {
    const response = await axiosInstance.put(endpoint, data);
    return response.data;
  },

  async delete<T>(endpoint: string): Promise<T> {
    const response = await axiosInstance.delete(endpoint);
    return response.data;
  },
};

export default apiService;
